import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import isArray from 'lodash/isArray'

import { Coordinates2D, VIEW_MODE } from '../types'
import { selectCurrentOverlayChunkIds } from './2D'
import { DocumentsState, selectActiveDocumentMapping } from './documents'
import { DocumentMapping } from '../../models/documentMapping'
import { IMasterSetPlanOption, IMasterSetPlanToCreate, IMaterialModificationConflict } from '../../models/masterSetPlan'
import { Project } from '../../models/project'
import { RootState } from '../../stores'

export type MasterSetPlanRegionFormFields = {
    name: string | null
    type: string | null
    option: string | null
    scale?: string
    floorLevel?: string | null
}

export type MasterSetPlanState = {
    masterSetPlanOptions: IMasterSetPlanOption[]
    masterSetPlanToCreate: IMasterSetPlanToCreate[]
    activeMasterSetPlanOptionId: number | null
    masterSetPlanFormDataState: MasterSetPlanRegionFormFields
    materialModificationConflicts: IMaterialModificationConflict[]
    materialModificationConflictsToSave: IMaterialModificationConflict[]
    savedMaterialModifications: IMaterialModificationConflict[]
    isConflictResolutionComplete: boolean
    isOverlaidChunkLocked: boolean
    areConflictsBeingReviewed: boolean
}

export const initialMasterSetPlanRegionFormState: MasterSetPlanRegionFormFields = {
    name: null,
    type: null,
    option: null,
    scale: undefined,
    floorLevel: null,
}

export const initialMasterSetPlanState: MasterSetPlanState = {
    activeMasterSetPlanOptionId: null,
    masterSetPlanFormDataState: initialMasterSetPlanRegionFormState,
    masterSetPlanOptions: [],
    masterSetPlanToCreate: [],
    materialModificationConflicts: [],
    materialModificationConflictsToSave: [],
    savedMaterialModifications: [],
    isConflictResolutionComplete: false,
    isOverlaidChunkLocked: false,
    areConflictsBeingReviewed: false,
}

const masterSetPlanSlice = createSlice({
    name: 'masterSetPlan',
    initialState: initialMasterSetPlanState,
    reducers: {
        addMasterSetPlanOptionToStore: (
            state: MasterSetPlanState,
            action: PayloadAction<IMasterSetPlanOption | IMasterSetPlanOption[]>
        ) => {
            state.masterSetPlanOptions = isArray(action.payload)
                ? [...state.masterSetPlanOptions, ...action.payload]
                : [...state.masterSetPlanOptions, action.payload]
        },

        setMasterSetPlanToCreate: (state: MasterSetPlanState, action: PayloadAction<IMasterSetPlanToCreate[]>) => {
            state.masterSetPlanToCreate = [...action.payload]
        },

        resetMasterSetPlanFormState: (state: MasterSetPlanState) => {
            return {
                ...state,
                masterSetPlanRegionFormState: initialMasterSetPlanRegionFormState,
                activeMasterSetPlanOptionId: null,
            }
        },

        resetMasterSetPlanOption: (state: MasterSetPlanState) => {
            return {
                ...state,
                masterSetPlanOptions: [],
            }
        },

        removeMasterSetPlanOptionById: (state: MasterSetPlanState, action: PayloadAction<number>) => {
            state.masterSetPlanOptions = state.masterSetPlanOptions.filter((option) => option.id !== action.payload)
        },

        updateMasterSetPlanOptions: (
            state: MasterSetPlanState,
            action: PayloadAction<IMasterSetPlanOption | IMasterSetPlanOption[]>
        ) => {
            state.masterSetPlanOptions = isArray(action.payload) ? action.payload : [action.payload]
        },

        setActiveMasterSetPlanOption: (state: MasterSetPlanState, action: PayloadAction<number | null>) => {
            state.activeMasterSetPlanOptionId = action.payload

            const masterSetPlanOption = state.masterSetPlanOptions.find((o) => o.id === action.payload)

            if (masterSetPlanOption) {
                state.masterSetPlanFormDataState.name = masterSetPlanOption.name
                state.masterSetPlanFormDataState.type = masterSetPlanOption.type
                state.masterSetPlanFormDataState.scale = masterSetPlanOption.scale
            }
        },

        updateMasterSetPlanFormState: (
            state: MasterSetPlanState,
            action: PayloadAction<Partial<MasterSetPlanRegionFormFields>>
        ) => {
            state.masterSetPlanFormDataState = {
                ...state.masterSetPlanFormDataState,
                ...action.payload,
            }
        },
        updateSingleMasterSetPlanState: (state: MasterSetPlanState, action: PayloadAction<IMasterSetPlanOption>) => {
            state.masterSetPlanOptions = state.masterSetPlanOptions.map((m) =>
                m.id === action.payload.id ? action.payload : m
            )
        },

        setConflictedMaterialOptionModifications(
            state: MasterSetPlanState,
            action: PayloadAction<IMaterialModificationConflict[]>
        ): void {
            state.materialModificationConflicts = action.payload
        },

        setSavedMaterialModifications(
            state: MasterSetPlanState,
            action: PayloadAction<IMaterialModificationConflict[]>
        ): void {
            state.savedMaterialModifications = action.payload
        },
        removeSavedMaterialModification(state: MasterSetPlanState, action: PayloadAction<number[]>) {
            state.savedMaterialModifications = [...state.savedMaterialModifications].filter(
                (mod) => !action.payload.includes(mod.id)
            )
        },
        updateSavedMaterialModification(
            state: MasterSetPlanState,
            action: PayloadAction<IMaterialModificationConflict>
        ) {
            state.savedMaterialModifications = [
                ...state.savedMaterialModifications.filter((mod) => mod.id !== action.payload.id),
                action.payload,
            ]
        },
        resetMaterialModificationConflicts(state: MasterSetPlanState): void {
            state.materialModificationConflicts = []
            state.materialModificationConflictsToSave = []
        },
        updateMaterialModificationConflictToSave(
            state: MasterSetPlanState,
            action: PayloadAction<IMaterialModificationConflict[]>
        ): void {
            const newConflicts = action.payload
            const newConflictIds = new Set(newConflicts.map((conflict) => conflict.id))

            state.materialModificationConflictsToSave = [
                ...state.materialModificationConflictsToSave.filter(
                    (existingConflict) => !newConflictIds.has(existingConflict.id)
                ),
                ...newConflicts,
            ]
        },
        // Drops the given options from pending conflicts (and their queued saves) at a location, so a
        // pending edit can't later be saved over a deletion made for the same location and options.
        discardMaterialModificationConflicts(
            state: MasterSetPlanState,
            action: PayloadAction<{ material_location_id: number; option_ids: number[] }[]>
        ): void {
            const discard = (conflicts: IMaterialModificationConflict[]) =>
                conflicts.reduce((kept, conflict) => {
                    const deleted = action.payload.filter(
                        (scope) =>
                            scope.material_location_id === conflict.material_location_id &&
                            scope.option_ids.some((id) => conflict.option_ids.includes(id))
                    )
                    const deletedOptionIds = deleted.flatMap((scope) => scope.option_ids)
                    const keptOptionIds = conflict.option_ids.filter((id) => !deletedOptionIds.includes(id))

                    if (!deleted.length) kept.push(conflict)
                    else if (keptOptionIds.length) kept.push({ ...conflict, option_ids: keptOptionIds })

                    return kept
                }, [] as IMaterialModificationConflict[])

            state.materialModificationConflicts = discard(state.materialModificationConflicts)
            state.materialModificationConflictsToSave = discard(state.materialModificationConflictsToSave)
        },
        updateConflictResolutionFlag(state: MasterSetPlanState, action: PayloadAction<boolean>) {
            state.isConflictResolutionComplete = action.payload
        },
        updateOverlaidChunkLockedFlag(state: MasterSetPlanState, action: PayloadAction<boolean>) {
            state.isOverlaidChunkLocked = action.payload
        },
        updateReviewingConflictsFlag(state: MasterSetPlanState, { payload }: PayloadAction<boolean>) {
            state.areConflictsBeingReviewed = payload
        },
    },
})

export const {
    addMasterSetPlanOptionToStore,
    resetMasterSetPlanOption,
    resetMasterSetPlanFormState,
    removeMasterSetPlanOptionById,
    updateMasterSetPlanOptions,
    setActiveMasterSetPlanOption,
    updateMasterSetPlanFormState,
    setMasterSetPlanToCreate,
    updateSingleMasterSetPlanState,
    setConflictedMaterialOptionModifications,
    setSavedMaterialModifications,
    resetMaterialModificationConflicts,
    updateMaterialModificationConflictToSave,
    discardMaterialModificationConflicts,
    updateConflictResolutionFlag,
    updateOverlaidChunkLockedFlag,
    removeSavedMaterialModification,
    updateSavedMaterialModification,
    updateReviewingConflictsFlag,
} = masterSetPlanSlice.actions

export default masterSetPlanSlice

function determineFormNameValue(parentMapping: DocumentMapping | null, formDataType: string | null) {
    if (formDataType) return formDataType

    if (parentMapping && parentMapping.type) return parentMapping.type

    return null
}

export const selectMasterSetPlanToCreate = createSelector(
    [
        (state: RootState) => state.IMUP.masterSetPlan.masterSetPlanToCreate,
        (state: RootState) => state.IMUP.masterSetPlan.masterSetPlanOptions,
        (state: RootState) => state.IMUP.masterSetPlan.activeMasterSetPlanOptionId,
        (state: RootState) => state.IMUP.masterSetPlan.masterSetPlanFormDataState,
        (state: RootState) => selectActiveDocumentMapping(state),
    ],
    (optionsOrBaseHomesToCreate, allOptionsOrBaseHomes, activeOptionId, formData, activeMapping) => {
        const mappingType = determineFormNameValue(activeMapping, formData.type)

        return {
            masterSetPlanToCreate: optionsOrBaseHomesToCreate,
            masterSetPlanFormDataState: {
                name: formData.name,
                type: mappingType,
                scale: formData.scale,
                option: formData.option,
                floorLevel: formData.floorLevel,
            },
            masterSetPlanOptions: allOptionsOrBaseHomes,
            activeMasterSetPlanOptionId: activeOptionId,
        }
    }
)

const generateCoordinatesFromMappingData = (mappingAdditionalData: DocumentMapping['coordinates']): Coordinates2D => {
    const { x, y, width, height } = mappingAdditionalData

    // Top left => Top right going counter-clockwise
    return [
        [x, y],
        [x, y + height],
        [x + width, y + height],
        [x + width, y],
    ] as Coordinates2D
}

export const selectMasterSetPlanOptionsForActivePage = createSelector(
    [
        (state: RootState) => state.IMUP.documents.projectDocumentMappings,
        (state: RootState) => state.IMUP.documents.activeDocumentChunkId,
    ],
    (documentMappings, activeFloorDocId) => {
        if (!documentMappings) return [] as IMasterSetPlanOption[]

        const activeMapping = documentMappings.find((map) => map.document_chunk_id === activeFloorDocId)

        if (!activeMapping) return [] as IMasterSetPlanOption[]

        const mappingsRelatedToActivePage = documentMappings.filter((mapping) => {
            return mapping.additional_data.parentDocumentMapping === activeMapping.id
        })

        if (mappingsRelatedToActivePage.length === 0) return [] as IMasterSetPlanOption[]

        return mappingsRelatedToActivePage.reduce((opts, mapping) => {
            return [
                ...opts,
                {
                    name: mapping.page_name ?? '',
                    scale: mapping.scale_factor,
                    type: mapping.type,
                    isOption: mapping.is_option,
                    coordinates: generateCoordinatesFromMappingData(mapping.coordinates),
                    document_chunk_id: mapping.document_chunk_id,
                    id: mapping.id,
                },
            ]
        }, [] as IMasterSetPlanOption[])
    }
)

export const selectMasterSetPlanOptions = createSelector(
    (state: RootState) => state.IMUP.masterSetPlan.masterSetPlanOptions,
    (options) => options
)

export const selectAreConflictsBeingReviewed = createSelector(
    (state: RootState) => state.IMUP.masterSetPlan.areConflictsBeingReviewed,
    (flag) => flag
)

export const selectAreConflictsResolutionComplete = createSelector(
    (state: RootState) => state.IMUP.masterSetPlan.isConflictResolutionComplete,
    (flag) => flag
)

export const selectIsOverlaidChunkLocked = createSelector(
    (state: RootState) => state.IMUP.masterSetPlan.isOverlaidChunkLocked,
    (flag) => flag
)

export const selectIsConflictResolutionInProgress = createSelector(
    [
        (state: { IMUP: { masterSetPlan: MasterSetPlanState } }) => state.IMUP.masterSetPlan,
        (state: { IMUP: { '2D'?: { currentEstimatedOptionOverlayChunkIds: number[] } } }) =>
            state.IMUP['2D']?.currentEstimatedOptionOverlayChunkIds?.length ?? 0,
    ],
    ({ isConflictResolutionComplete, isOverlaidChunkLocked, areConflictsBeingReviewed }, estimatedOverlayCount) =>
        // Loading a resolved option resets completion without resetting the lock.
        // Only an explicit review may re-enter conflict resolution in that view.
        areConflictsBeingReviewed || (!estimatedOverlayCount && isOverlaidChunkLocked && !isConflictResolutionComplete)
)

export type MaterialInteractionState = {
    IMUP: {
        common: { activeMode: VIEW_MODE | null }
        project: { project: Pick<Project, 'is_master_set'> | null }
        documents: Pick<DocumentsState, 'activeFloorDocId' | 'projectDocumentMappings'>
        '2D': { currentOverlayChunkIds: number[]; currentEstimatedOptionOverlayChunkIds: number[] }
        masterSetPlan: MasterSetPlanState
    }
}

// Deleting an opening deletes all its locations, so every location must belong
// to an editable option before allowing it through a resolved overlay's UI.
export const isMaterialOpeningInOptions = (
    opening: { opening_locations: { document_chunk_id: number }[] },
    optionChunkIds: ReadonlySet<number>
): boolean =>
    opening.opening_locations.length > 0 &&
    opening.opening_locations.every((location) => optionChunkIds.has(location.document_chunk_id))

// Save opens the editable conflict representations; Resolve Conflicts switches to
// the estimated overlay view. Neither phase is the same as positioning an overlay.
export const selectAreMaterialInteractionsBlocked = createSelector(
    [
        (state: MaterialInteractionState) => state.IMUP.project.project?.is_master_set,
        (state: MaterialInteractionState) => state.IMUP.common.activeMode,
        (state: MaterialInteractionState) => state.IMUP['2D'].currentOverlayChunkIds?.length ?? 0,
        (state: MaterialInteractionState) => state.IMUP.masterSetPlan.isOverlaidChunkLocked,
        (state: MaterialInteractionState) => state.IMUP.masterSetPlan.areConflictsBeingReviewed,
    ],
    (isMasterSet, activeMode, overlayCount, isLocked, isReviewing) => {
        if (!isMasterSet || activeMode !== VIEW_MODE.Markup2D || isReviewing) return false

        return overlayCount > 0 && !isLocked
    }
)

// Conflict geometry remains editable, but it must never activate a material
// group or its settings. Normal group editing resumes after conflicts finish.
export const selectIsMaterialGroupActivationBlocked = createSelector(
    [
        selectAreMaterialInteractionsBlocked,
        selectIsConflictResolutionInProgress,
        (state: MaterialInteractionState) => state.IMUP.project.project?.is_master_set,
        (state: MaterialInteractionState) => state.IMUP.common.activeMode,
    ],
    (areInteractionsBlocked, isResolving, isMasterSet, activeMode) =>
        areInteractionsBlocked || (!!isMasterSet && activeMode === VIEW_MODE.Markup2D && isResolving)
)

// Resolved overlays allow option materials to be created and edited. Protect the
// base materials separately instead of disabling the entire combined canvas.
export const selectAreBaseHomeMaterialsReadOnly = createSelector(
    [
        (state: MaterialInteractionState) => state.IMUP.project.project?.is_master_set,
        (state: MaterialInteractionState) => state.IMUP.common.activeMode,
        (state: MaterialInteractionState) => state.IMUP['2D'].currentOverlayChunkIds?.length ?? 0,
        (state: MaterialInteractionState) => state.IMUP['2D'].currentEstimatedOptionOverlayChunkIds?.length ?? 0,
        (state: MaterialInteractionState) => state.IMUP.masterSetPlan,
    ],
    (isMasterSet, activeMode, overlayCount, estimatedOverlayCount, masterSetPlan) =>
        !!isMasterSet &&
        activeMode === VIEW_MODE.Markup2D &&
        !masterSetPlan.areConflictsBeingReviewed &&
        (estimatedOverlayCount > 0 ||
            (overlayCount > 0 && masterSetPlan.isOverlaidChunkLocked && masterSetPlan.isConflictResolutionComplete))
)

export const selectMaterialModificationConflicts = createSelector(
    (state: RootState) => {
        return {
            materialModificationConflicts: state.IMUP.masterSetPlan.materialModificationConflicts,
            materialModificationConflictsToSave: state.IMUP.masterSetPlan.materialModificationConflictsToSave,
        }
    },
    (state) => state
)

export const selectSavedMaterialModifications = createSelector(
    [
        (state: RootState) => state.IMUP.masterSetPlan.savedMaterialModifications,
        selectCurrentOverlayChunkIds,
        (state: RootState) => state.IMUP['2D'].currentEstimatedOptionOverlayChunkIds ?? [],
    ],
    (modifications, activeChunkIds, activeEstimatedOptionChunkIds) => {
        const activeIds = new Set([...activeChunkIds, ...activeEstimatedOptionChunkIds])

        return modifications.filter((mod) => mod.option_ids.some((id) => activeIds.has(id)))
    }
)
